import * as React from "react";
import { repositoryMdm } from "../../../../_base/_const/_constVar";
import moment from "moment";
import type { MessageResponse } from "../../../../model/MessageResponse";
import type { SelectListItem } from "../../../../model/SelectListItem";
import type { MasterOrg } from "../api/saleOutwardApi";
import type { SaleOrderInfo, SaleOutwardHeader } from "../types";
import { MAX_DESCRIPTION_LENGTH } from "../config/saleKinds";
import { TABLE_HEAD_HEIGHT, TOP_BAR_HEIGHT } from "../config/theme";
import OutwardHeading from "./OutwardHeading";

interface SaleOrderInfoPanelProps {
  /** Tên loại phiếu hiện ở đầu khối (Xuất bán / Xuất đổi trả) */
  kindLabel: string;
  header: SaleOutwardHeader;
  order: SaleOrderInfo;
  masterOrgs: MasterOrg[];
  editDisabled: boolean;
  onChangeDescription: (value: string) => void;
  /** Cột bên phải ghép sát khối này (không khoảng cách): vuông 2 góc bên phải */
  joinedRight?: boolean;
}

/** Thông tin giao vận lưu dạng JSON trong attribute3 của đơn */
interface DeliveryInfo {
  customerId?: string | number | null;
  customerName?: string | null;
  telephoneNumber?: string | null;
  customerAddress?: string | null;
  customerCommuneId?: string | number | null;
  deliveryAppointmentTime?: string | null;
}

/** Tên tỉnh / phường xã mới, gắn với id xã đã tra để không hiện kết quả của đơn trước */
interface CommuneResult {
  id: string;
  province: string;
  ward: string;
}

/** Ngày giờ theo thiết kế (29/09/2026 15:42); ngày trống hoặc năm trước 2000 (mặc định của backend) để trống */
const formatDateTime = (value: string | Date | null | undefined) => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() && date.year() >= 2000 ? date.format("DD/MM/YYYY HH:mm") : "";
};

const C = {
  card: "#FFFFFF",
  border: "#DDE0DA",
  divider: "#ECEEE9",
  headBg: "#F6F7F4",
  text: "#1B1F24",
  textSub: "#5C6570",
  label: "#6B737C",
  muted: "#8A929B",
  empty: "#A3AAB2",
  accent: "#1F5FAE",
  focusRing: "#E3ECF7",
  inputBorder: "#D5D9D2",
  danger: "#B42318",
  dangerBg: "#FDF1F0",
};

const S: Record<string, React.CSSProperties> = {
  section: {
    position: "relative",
    width: 472,
    maxWidth: "100%",
    maxHeight: "100%",
    flexShrink: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    color: C.text,
    fontSize: 13,
    lineHeight: "normal",
  },
  // Cao bằng thanh Bên xuất bên phải (gồm viền dưới) để hàng Bên xuất / Bên nhận thẳng hàng tiêu đề lưới serial
  heading: {
    flexShrink: 0,
    boxSizing: "border-box",
    minHeight: TOP_BAR_HEIGHT,
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    borderBottom: `1px solid ${C.divider}`,
  },
  body: { flex: "1 1 auto", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" },
  sides: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    // Bằng hàng tiêu đề AG Grid: chiều cao cấu hình + 1px viền dưới
    boxSizing: "border-box",
    height: TABLE_HEAD_HEIGHT + 1,
    borderBottom: `1px solid ${C.divider}`,
  },
  sideHead: {
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    background: C.headBg,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: C.textSub,
  },
  rows: { margin: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  cell: { padding: "8px 16px", minWidth: 0 },
  cellRight: { padding: "8px 16px", minWidth: 0, borderLeft: `1px solid ${C.divider}` },
  field: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  dt: { color: C.label, fontSize: 12, fontWeight: 400 },
  dd: { margin: 0, fontWeight: 500, color: C.text, overflowWrap: "anywhere" },
  footer: {
    padding: "14px 16px",
    borderTop: `1px solid ${C.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  noteBox: { display: "flex", flexDirection: "column", gap: 6 },
  noteLabel: { color: C.label, fontSize: 12 },
  noteError: { color: C.danger, fontSize: 12 },
};

/** Ẩn khỏi màn hình nhưng trình đọc màn hình vẫn đọc */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const noteStyle = (focused: boolean, invalid: boolean, disabled: boolean): React.CSSProperties => ({
  width: "100%",
  boxSizing: "border-box",
  font: "inherit",
  resize: "none",
  border: `1px solid ${invalid ? C.danger : focused ? C.accent : C.inputBorder}`,
  borderRadius: 8,
  padding: "8px 10px",
  outline: "none",
  boxShadow: focused ? `0 0 0 3px ${invalid ? C.dangerBg : C.focusRing}` : "none",
  background: disabled ? C.headBg : C.card,
  color: disabled ? C.textSub : C.text,
  cursor: disabled ? "not-allowed" : undefined,
});


const toText = (value: unknown) => (value == null ? "" : String(value).trim());

/** Ghép mã và tên, bỏ phần trống */
const joinParts = (...parts: unknown[]) => parts.map(toText).filter(Boolean).join(" · ");

/** Đọc attribute3; chuỗi hỏng hoặc không phải object thì bỏ qua */
const parseDeliveryInfo = (raw: unknown): DeliveryInfo | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as DeliveryInfo) : null;
  } catch {
    return null;
  }
};

/** attribute5 là thông tin người mua dạng "tên - sđt" */
const parseBuyer = (raw: unknown) => {
  const parts = toText(raw).split(" - ");
  return { name: parts[0]?.trim() || "", phone: parts[1]?.trim() || "" };
};

const getDeliveryText = (delivery: string) => {
  switch (delivery) {
    case "KHONG":
      return "Nhận tại cửa hàng";
    case "NOI_BO":
    case "HA_COM":
      return "Nội bộ";
    default:
      return delivery;
  }
};

/** Id phường xã mới như trang cũ: đơn có ĐVTN lấy trong attribute3, sau đó id xã mới, rồi id xã cũ */
const pickCommuneId = (order: SaleOrderInfo, delivery: DeliveryInfo | null) => {
  if (order.dvtn) return toText(delivery?.customerCommuneId);
  if (order.newCommuneId) return toText(order.newCommuneId);
  if (order.cusCommuneId) return toText(order.cusCommuneId);
  return "";
};

interface FieldProps {
  label: string;
  value: string;
  multiline?: boolean;
  pending?: boolean;
}

/** Một cặp nhãn nhỏ ở trên, giá trị ở dưới; trống thì hiện "—" */
const Field = ({ label, value, multiline, pending }: FieldProps) => (
  <div style={S.field}>
    <dt style={S.dt}>{label}</dt>
    <dd style={{ ...S.dd, lineHeight: multiline ? 1.45 : undefined }}>
      {value || (
        <span style={{ color: pending ? C.muted : C.empty }}>
          {pending ? "Đang tải…" : "—"}
        </span>
      )}
    </dd>
  </div>
);

/**
 * Khối "Thông tin chung" của phiếu xuất bán: bên xuất, bên nhận, diễn giải chứng từ gốc và ghi chú phiếu.
 * Giữ đủ các trường và cách lấy dữ liệu của SaleOrderOutward (trang cũ), trình bày theo thiết kế mới.
 */
const SaleOrderInfoPanel = ({
  kindLabel,
  header,
  order,
  masterOrgs,
  editDisabled,
  onChangeDescription,
  joinedRight = false,
}: SaleOrderInfoPanelProps) => {
  const titleId = React.useId();
  const noteId = React.useId();
  const noteErrorId = React.useId();
  const [noteFocused, setNoteFocused] = React.useState(false);
  const [commune, setCommune] = React.useState<CommuneResult | null>(null);

  const delivery = React.useMemo(() => parseDeliveryInfo(order.attribute3), [order.attribute3]);
  const isDvtn = !!order.dvtn;
  const buyer = parseBuyer(order.attribute5);
  const communeId = pickCommuneId(order, delivery);

  // Tra tỉnh / phường xã mới theo id xã; id đổi thì bỏ kết quả của lần tra trước
  React.useEffect(() => {
    if (!communeId) return undefined;
    let cancelled = false;
    repositoryMdm
      .get<MessageResponse<SelectListItem[]>>(
        `/api/v1/TblDmCommuneMoi/get-select?Ids=${encodeURIComponent(communeId)}`,
      )
      .then((res) => {
        if (cancelled) return;
        const first = res?.success && Array.isArray(res.data) ? res.data[0] : undefined;
        setCommune({ id: communeId, province: toText(first?.att1), ward: toText(first?.text) });
      })
      .catch(() => {
        if (!cancelled) setCommune({ id: communeId, province: "", ward: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [communeId]);

  const currentCommune = commune?.id === communeId ? commune : null;
  const communePending = !!communeId && !currentCommune;

  const orgCode = masterOrgs.find((org) => String(org.orgId) === String(order.fromBranchId))?.orgCode;
  const description = header.description ?? "";
  const noteTooLong = description.length > MAX_DESCRIPTION_LENGTH;
  const noteActive = noteFocused && !editDisabled;

  // Thứ tự trường theo khối Thông tin chung của trang cũ (SaleOrderOutward), từng cột từ trên xuống
  const rows: [React.ReactNode, React.ReactNode][] = [
    [
      <Field label="Thời gian tạo" value={formatDateTime(order.createDate)} />,
      <Field label="Mã số khách hàng" value={toText(isDvtn ? delivery?.customerId : order.cusCode)} />,
    ],
    [
      <Field label="Thời gian hoàn thành" value={formatDateTime(header.finishDate)} />,
      <Field label="Khách hàng" value={toText(isDvtn ? delivery?.customerName : buyer.name || order.cusName)} />,
    ],
    [
      <Field label="Mã phiếu xuất kho" value={toText(header.transactionCode)} />,
      <Field
        label="Số điện thoại"
        value={toText(isDvtn ? delivery?.telephoneNumber : buyer.phone || order.cusTelephone)}
      />,
    ],
    [
      <Field label="Số chứng từ" value={toText(header.sourceCode)} />,
      <Field label="Tỉnh Thành" value={currentCommune?.province ?? ""} pending={communePending} />,
    ],
    [
      <Field label="Chi nhánh" value={joinParts(orgCode, order.fromBranchName)} />,
      <Field label="Phường Xã" value={currentCommune?.ward ?? ""} pending={communePending} />,
    ],
    [
      <Field label="Địa điểm làm việc" value={toText(order.fromBranchAddress)} multiline />,
      <Field
        label="Địa chỉ cụ thể"
        value={toText(isDvtn ? delivery?.customerAddress : order.cusAddress)}
        multiline
      />,
    ],
    [
      <Field label="Kho" value={joinParts(order.fromInvCode, order.fromInvName)} />,
      <Field
        label="Thời gian nhận"
        value={formatDateTime(isDvtn ? delivery?.deliveryAppointmentTime : order.receiveDate)}
      />,
    ],
    [<Field label="Người lập" value={order.createName ? joinParts(order.createName, order.createCode) : ""} />, null],
    [<Field label="Chức danh" value={toText(order.createRoleName)} />, null],
    [<Field label="Hình thức giao" value={getDeliveryText(toText(order.delivery))} />, null],
  ];

  return (
    <section
      aria-labelledby={titleId}
      style={joinedRight ? { ...S.section, borderTopRightRadius: 0, borderBottomRightRadius: 0 } : S.section}
    >
      <h2 id={titleId} style={VISUALLY_HIDDEN}>
        Thông tin chung
      </h2>
      <div style={S.heading}>
        <OutwardHeading kindLabel={kindLabel} status={header.status} />
      </div>
      <div style={S.body}>
        <div style={S.sides}>
          <div style={S.sideHead}>Bên xuất</div>
          <div style={{ ...S.sideHead, borderLeft: `1px solid ${C.divider}` }}>Bên nhận</div>
        </div>
        {/* Mỗi hàng một cặp Bên xuất / Bên nhận nên nhãn hai bên luôn thẳng hàng, kể cả khi giá trị xuống dòng.
            Hàng cuối không kẻ vì phần chân đã có đường kẻ trên */}
        <dl style={S.rows}>
          {rows.map(([left, right], index) => {
            const rowLine = index < rows.length - 1 ? `1px solid ${C.divider}` : undefined;
            return (
              <React.Fragment key={index}>
                <div style={{ ...S.cell, borderBottom: rowLine }}>{left}</div>
                <div style={{ ...S.cellRight, borderBottom: rowLine }}>{right}</div>
              </React.Fragment>
            );
          })}
        </dl>
        <div style={S.footer}>
          <dl style={{ margin: 0 }}>
            <Field label="Diễn giải chứng từ gốc" value={toText(header.sourceDescription)} multiline />
          </dl>
          <div style={S.noteBox}>
            <label htmlFor={noteId} style={S.noteLabel}>
              Ghi chú phiếu
            </label>
            <textarea
              id={noteId}
              rows={2}
              value={description}
              disabled={editDisabled}
              aria-invalid={noteTooLong || undefined}
              aria-describedby={noteTooLong ? noteErrorId : undefined}
              onChange={(e) => onChangeDescription(e.target.value)}
              onFocus={() => setNoteFocused(true)}
              onBlur={() => setNoteFocused(false)}
              style={noteStyle(noteActive, noteTooLong, editDisabled)}
            />
            {noteTooLong && (
              <div id={noteErrorId} style={S.noteError}>
                Ghi chú không được vượt quá 300 kí tự!
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(SaleOrderInfoPanel);
